import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { PricingPlanType } from "../../types/pricingPlans";
import { usePricingPlan } from "../../hooks/usePricingPlan";
import SubscriptionsAPI from "../../api/SubscriptionsAPI";
import PageWrapper from "../PageWrapper/PageWrapper";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { toastService } from "../../services/ToastService";
import { getSubscriptionStatusKey, isSubscriptionPaymentFailed } from "../../utils/subscriptionStatusMapper";
import { generatePricingPlan, getThemeColorClasses } from "../../utils/pricingPlanUtils";

const PricingPlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentPlan, isLoadingSubscription, currentSubscription } = usePricingPlan();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const state = searchParams.get('state');
    if (state === 'success') {
      toastService.showToast(
        t('subscription.success_message', 'Subscription updated successfully! Changes may take a few minutes to take effect.'),
        'success'
      );
      setSearchParams({});
    } else if (state === 'cancel') {
      toastService.showToast(
        t('subscription.cancel_message', 'Subscription update was cancelled. No changes were made.'),
        'error'
      );
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t]);

  const plans = ['free', 'store', 'brand', 'advanced-store'].map(planId => {
    const plan = generatePricingPlan(planId as PricingPlanType, planId === currentPlan.id);
    return {
      ...plan,
      name: t(plan.name, plan.name),
      description: t(plan.description, plan.description),
      features: plan.features.map(featureKey => t(featureKey, featureKey)),
      featuresNotIncluded: plan.featuresNotIncluded.map(featureKey => t(featureKey, featureKey)),
      note: plan.note ? t(plan.note, plan.note) as string : null,
      price: plan.price === 'Free' ? 'Free' : t(plan.price, plan.price) as string
    };
  });

  // Mark current plan
  plans.forEach(plan => {
    plan.isCurrentPlan = plan.id === currentPlan.id;
  });

  const isDowngrade = (planId: PricingPlanType, currentPlanId: PricingPlanType): boolean => {
    const planHierarchy = ['free', 'store', 'brand', 'advanced-store'];
    const currentIndex = planHierarchy.indexOf(currentPlanId);
    const planIndex = planHierarchy.indexOf(planId);
    return planIndex < currentIndex;
  };

  const handleSubscribe = async (plan: PricingPlanType) => {
    setIsLoading(true);
    try {
      const response = await SubscriptionsAPI.createCheckoutOrPortal(plan);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create checkout or get portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await SubscriptionsAPI.createCheckoutOrPortal('current');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to get customer portal URL:', error);
    }
  };

  const canChangeTo = (planId: PricingPlanType) => {
    if (planId === 'early-adopter') return false;
    if (planId === currentPlan.id) return false;
    if (currentPlan.id === 'early-adopter' && planId === 'free') return false;
    
    return planId === 'free' || planId === 'store' || planId === 'brand' || planId === 'advanced-store';
  };


  if (isLoadingSubscription) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('subscription.loading', 'Loading subscription information...')}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const isPaymentFailed = isSubscriptionPaymentFailed(currentSubscription?.status);

  return (
    <PageWrapper>
      <div className="min-h-screen">
        <div className="container mx-auto">

          {/* Payment failed warning */}
          {isPaymentFailed && (
            <div className="mb-6 max-w-7xl mx-auto rounded-lg border border-red-300 bg-red-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-red-800 text-sm">
                {t('subscription.payment_failed_warning', 'Your subscription payment could not be processed. Please check funds in your account, update your card, or follow the link below to take action and avoid service interruption.')}
              </p>
              <PrimaryButton
                text={t('subscription.payment_failed_cta', 'Resolve payment') as string}
                fn={handleManageSubscription}
                actionName="Resolve subscription payment"
              />
            </div>
          )}

          {/* Current Plan Banner */}
          {currentPlan && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-8 max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">
                    {t('subscription.current_plan', 'Current Plan')}: {currentPlan.name}
                  </h3>
                  <p className="text-blue-700 text-sm">{currentPlan.description}</p>
                  {currentSubscription && (
                    <div className="mt-2 text-xs text-blue-600">
                      <span className="inline-block bg-blue-100 px-2 py-1 rounded-full mr-2">
                        {t('subscription.status', 'Status:')} {t(getSubscriptionStatusKey(currentSubscription.status), 'Unknown')}
                      </span>
                      {currentSubscription.currentPeriodEnd && !currentSubscription.cancelAtPeriodEnd && (
                        <span className="inline-block bg-blue-100 px-2 py-1 rounded-full">
                          {t('subscription.renews', 'Renews:')} {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                      {currentSubscription.cancelAtPeriodEnd && currentSubscription.currentPeriodEnd && (
                        <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded-full ml-2">
                          {t('subscription.cancels_at_period_end', 'Cancels at period end')}: {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {/* Only show Manage Subscription button for paid plans */}
                {(currentPlan.id === 'store' || currentPlan.id === 'brand' || currentPlan.id === 'advanced-store') && (
                  <SecondaryButton
                    text={t('subscription.manage', 'Manage Subscription') || 'Manage Subscription'}
                    fn={handleManageSubscription}
                  />
                )}
              </div>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
            {plans.map((plan) => {
              const themeClasses = getThemeColorClasses(plan.themeColor);
              return (
                <div 
                  key={plan.id} 
                  className="relative group"
                >
                  {/* Card Container */}
                  <div className="relative h-full bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden flex flex-col">
                    
                    {/* Card Header */}
                    <div className="p-6 sm:p-8 pb-4 sm:pb-6">
                      <div className="text-center mb-4 sm:mb-6">
                        <h3 className={`text-xl sm:text-2xl font-bold ${themeClasses.text} mb-3`}>
                          {plan.name}
                        </h3>
                        {plan.isCurrentPlan && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            {t('subscription.current_plan_badge', 'Current Plan')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="px-8 flex-1">
                      <div className="space-y-4 mb-8">
                        {[...plan.features, ...plan.featuresNotIncluded].map((feature, index) => {
                          const isIncluded = !plan.featuresNotIncluded.includes(feature);
                          return (
                            <div key={index} className="flex items-start gap-3">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                                isIncluded ? `${themeClasses.bg}` : 'bg-gray-200'
                              }`}>
                                {isIncluded ? (
                                  <Check className="w-3 h-3 text-white" />
                                ) : (
                                  <X className="w-3 h-3 text-gray-400" />
                                )}
                              </div>
                              <span className={`text-sm leading-relaxed ${
                                isIncluded ? 'text-gray-900' : 'text-gray-400'
                              }`}>
                                {feature}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bottom Wave Section - Covers pricing and button */}
                     <div className="relative">
                       {/* Wave SVG - Taller to better cover pricing and button */}
                       <svg 
                         className="w-full h-64" 
                         viewBox="0 0 400 256" 
                         preserveAspectRatio="none"
                       >
                         <path
                           d="M0,256 L0,128 Q100,64 200,128 T400,128 L400,256 Z"
                           fill={themeClasses.bg === 'bg-emerald-600' ? '#059669' : 
                                 themeClasses.bg === 'bg-blue-600' ? '#2563eb' : 
                                 themeClasses.bg === 'bg-purple-600' ? '#9333ea' :
                                 themeClasses.bg === 'bg-amber-600' ? '#d97706' : '#4b5563'}
                           className="drop-shadow-lg"
                         />
                       </svg>
                       
                       {/* Pricing Content - Positioned in upper part of wave with text shadows */}
                       <div className="absolute top-12 left-0 right-0 flex flex-col items-center justify-center text-gray-600">
                         <div className="text-center">
                           {plan.id !== 'free' && (
                             <p className="text-sm font-medium mb-2 drop-shadow-md">{t('pricing.only_with', 'Only With')}</p>
                           )}
                           <div className="text-4xl font-bold drop-shadow-lg">
                             {plan.id === 'free' ? plan.price : `${plan.price}`}
                           </div>
                           {plan.id !== 'free' && (
                             <p className="text-sm opacity-95 drop-shadow-md">{t('pricing.per_month', 'per month')}</p>
                           )}
                         </div>
                       </div>

                       {/* Action Button - Positioned in lower part of wave */}
                       <div className="absolute bottom-12 left-0 right-0 flex justify-center">
                         {plan.isCurrentPlan ? (
                           <PrimaryButton
                             text={t('subscription.current_plan_badge', 'Current Plan') as string}
                             isDisabled={true}
                             fn={() => {}}
                             actionName={t('subscription.current_plan_badge', 'Current Plan') as string}
                           />
                         ) : canChangeTo(plan.id) ? (
                           <PrimaryButton
                             text={
                              isDowngrade(plan.id, currentPlan.id)
                                ? (t('pricing.downgrade', 'Downgrade') as string)
                                : (t('pricing.upgrade', 'Upgrade Now') as string)
                            }
                             fn={() => handleSubscribe(plan.id)}
                             isDisabled={isLoading}
                             actionName={`${isDowngrade(plan.id, currentPlan.id) ? 'Downgrade' : 'Upgrade'} to ${plan.name}`}
                           />
                         ) : (
                           <PrimaryButton
                             text={t('pricing.not_available', 'Not Available') as string}
                             isDisabled={true}
                             fn={() => {}}
                             actionName={t('pricing.not_available', 'Not Available') as string}
                           />
                         )}
                       </div>
                     </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PricingPlansPage;
